import axios from 'axios';
import { API_BASE_URL } from '../../config';

// One thin wrapper per server route (spec 5.4). Components never call axios
// directly for vendors, so tests mock this module and nothing else.
const CFG = { withCredentials: true };
const base = `${API_BASE_URL}/api/vendors`;
const admin = `${API_BASE_URL}/api/admin/vendors`;

const get = (url, params) => axios.get(url, { ...CFG, params }).then((r) => r.data);
const post = (url, body) => axios.post(url, body || {}, CFG).then((r) => r.data);

// Read endpoints answer either a bare array or `{ <key>: [...] }`; normalise so
// callers always get an array.
export const listOf = (data, key) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return [];
};

// ---- Reads -----------------------------------------------------------------
export const getMe = () => get(`${base}/me`);
export const listVendors = (params) => get(base, params);
export const getVendor = (id) => get(`${base}/${id}`);
export const getSpecialities = () => get(`${base}/specialities`);
export const getCountries = () => get(`${base}/countries`);
export const getStates = (countryId) => get(`${base}/states`, { country_id: countryId });
export const getHistory = (id) => get(`${base}/${id}/history`);
export const getMessages = (id) => get(`${base}/${id}/messages`);
export const getDocuments = (id) => get(`${base}/${id}/documents`);

// The browser downloads these itself (Content-Disposition: attachment), so the
// client only builds the URL.
export const documentUrl = (id, attId) => `${base}/${id}/documents/${attId}`;

// The register export is fetched as a blob (not navigated to) so a 403/429
// reaches the caller as an error it can toast, instead of replacing the page
// with JSON. An error body arrives as a Blob; parse it so errorMessage works.
export async function exportVendorsCsv() {
  try {
    const res = await axios.get(`${base}/export.csv`, { ...CFG, responseType: 'blob' });
    const m = /filename="?([^";]+)"?/i.exec(res.headers?.['content-disposition'] || '');
    return { blob: res.data, filename: m ? m[1] : 'vendor-register.csv' };
  } catch (err) {
    const data = err?.response?.data;
    if (data && typeof data.text === 'function') {
      try { err.response.data = JSON.parse(await data.text()); } catch { err.response.data = {}; }
    }
    throw err;
  }
}

// Page a vendor query to completion, capped. `capped` says rows were left out.
export const MAX_ROWS = 500;
export async function listAllVendors(params) {
  const rows = [];
  let total = 0;
  for (let page = 1; rows.length < MAX_ROWS; page += 1) {
    const d = await listVendors({ ...params, page });
    const batch = listOf(d, 'vendors').length ? listOf(d, 'vendors') : listOf(d, 'rows');
    total = d?.total ?? batch.length;
    rows.push(...batch);
    if (!batch.length || rows.length >= total) break;
  }
  return { rows: rows.slice(0, MAX_ROWS), capped: total > MAX_ROWS };
}

// ---- Vendor actions ----------------------------------------------------------
export const postMessage = (id, text) => post(`${base}/${id}/messages`, { text });
export const uploadDocument = (id, file, docType) => {
  const form = new FormData();
  form.append('doc_type', docType);
  form.append('file', file);
  return axios.post(`${base}/${id}/documents`, form, CFG).then((r) => r.data);
};
export const markReviewed = (id, body) => post(`${base}/${id}/review`, body);
export const acknowledgeOutside = (id) => post(`${base}/${id}/acknowledge`);
export const tagVendor = (id, tagIds) => post(`${base}/${id}/tags`, { tag_ids: tagIds });

// ---- Proposals -----------------------------------------------------------------
export const createProposal = (body) => post(`${base}/proposals`, body);
export const listProposals = (scope) => get(`${base}/proposals`, { scope });
export const approveProposal = (id) => post(`${base}/proposals/${id}/approve`);
export const rejectProposal = (id, reason) => post(`${base}/proposals/${id}/reject`, { reason });
export const cancelProposal = (id) => post(`${base}/proposals/${id}/cancel`);

// ---- Access requests -----------------------------------------------------------
export const createAccessRequest = (body) => post(`${base}/access-requests`, body);
export const listAccessRequests = (scope) => get(`${base}/access-requests`, { scope });
export const approveAccessRequest = (id, note) => post(`${base}/access-requests/${id}/approve`, note ? { note } : {});
export const rejectAccessRequest = (id, note) => post(`${base}/access-requests/${id}/reject`, { note });
export const cancelAccessRequest = (id) => post(`${base}/access-requests/${id}/cancel`);

// ---- Admin (vendors.manage) ----------------------------------------------------
export const adminSearchUsers = (q) => get(`${admin}/users/search`, { q });
// Existing employee-facing department list (id, name); the drawer's picker source.
export const adminListDepartments = () => get(`${API_BASE_URL}/api/departments`);
export const adminListTags = () => get(`${admin}/tags`);
export const adminToggleSpeciality = (id, on) => axios.patch(`${admin}/tags/${id}`, { is_speciality: on }, CFG).then((r) => r.data);
export const adminCreateSpeciality = (name) => post(`${admin}/tags`, { name });
export const adminGetTag = (id) => get(`${admin}/tags/${id}`);
export const adminLinkDepartment = (id, deptId, level) => axios.put(`${admin}/tags/${id}/departments/${deptId}`, { level }, CFG).then((r) => r.data);
export const adminUnlinkDepartment = (id, deptId) => axios.delete(`${admin}/tags/${id}/departments/${deptId}`, CFG).then((r) => r.data);
export const adminAddOwner = (id, userId) => post(`${admin}/tags/${id}/owners`, { user_id: userId });
export const adminRemoveOwner = (id, userId) => axios.delete(`${admin}/tags/${id}/owners/${userId}`, CFG).then((r) => r.data);
export const adminGrant = (id, userId, level) => axios.put(`${admin}/tags/${id}/grants/${userId}`, { level }, CFG).then((r) => r.data);
export const adminRevokeGrant = (id, userId) => axios.delete(`${admin}/tags/${id}/grants/${userId}`, CFG).then((r) => r.data);
export const adminSyncNow = () => post(`${admin}/sync`);
export const adminSyncStatus = () => get(`${admin}/sync/status`);
export const adminOdooCheck = () => get(`${admin}/odoo-check`);
